import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../api';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
}));

const CreateBlog: React.FC = () => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [preview, setPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setImageFile(event.target.files[0]);
      setImageUrl(URL.createObjectURL(event.target.files[0])); // for preview
    }
  };

  const handleGenerateImage = async () => {
    if (!title || !content) {
      setError('Please enter a title and some content before generating an image.');
      return;
    }
    setError(null);
    setIsGenerating(true);
    try {
      const response = await api.post('/api/blogs/generate-image', { title, content });
      setImageUrl(response.data.imageUrl);
      setImageFile(null); // Clear any user-uploaded file
    } catch (err) {
      setError('Failed to generate image. Please try again.');
      console.error(err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleEnhanceText = async () => {
    if (!content) {
      setError('Please enter some content before enhancing.');
      return;
    }
    setError(null);
    setIsEnhancing(true);
    try {
      const response = await api.post('/api/blogs/enhance-text', { content });
      setContent(response.data.enhancedContent);
    } catch (err) {
      setError('Failed to enhance text. Please try again.');
      console.error(err);
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleSubmit = async () => {
    if (!title || !content) {
      setError('Title and content are required.');
      return;
    }
    setError(null);
    setSubmitting(true);

    const formData = new FormData();
    formData.append('title', title);
    formData.append('content', content);

    if (imageFile) {
      formData.append('image', imageFile);
    } else if (imageUrl) {
      formData.append('imageUrl', imageUrl);
    }

    try {
      await api.post('/api/blogs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      // Optionally, redirect or show a success message
      setTitle('');
      setContent('');
      setImageFile(null);
      setImageUrl(null);
      setPreview(false);
    } catch (err) {
      setError('Failed to submit blog post. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Create New Blog Post
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={preview ? 6 : 12}>
          <StyledPaper>
            <TextField
              label="Blog Title"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              margin="normal"
            />
            <Box my={2}>
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                placeholder="Write your blog content here..."
                style={{ height: '300px', marginBottom: '50px' }}
              />
            </Box>
            <Grid container spacing={2}>
                <Grid item>
                    <Button variant="contained" component="label">
                        Upload Image
                        <input type="file" hidden onChange={handleImageChange} accept="image/*" />
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        onClick={handleGenerateImage}
                        disabled={isGenerating}
                    >
                        {isGenerating ? <CircularProgress size={24} /> : 'Generate Image'}
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        onClick={handleEnhanceText}
                        disabled={isEnhancing}
                    >
                        {isEnhancing ? <CircularProgress size={24} /> : 'Enhance Text'}
                    </Button>
                </Grid>
            </Grid>
            {imageUrl && <Typography variant="body2" sx={{ mt: 1 }}>Image selected</Typography>}
          </StyledPaper>
        </Grid>
        {preview && (
          <Grid item xs={12} md={6}>
            <StyledPaper>
              <Typography variant="h5" gutterBottom>
                Preview
              </Typography>
              <Typography variant="h4" component="h1" gutterBottom>
                {title}
              </Typography>
              {imageUrl && (
                <Box
                  component="img"
                  sx={{
                    height: 233,
                    width: 350,
                    maxHeight: { xs: 233, md: 167 },
                    maxWidth: { xs: 350, md: 250 },
                    mb: 2,
                  }}
                  alt="Blog preview"
                  src={imageUrl}
                />
              )}
              <Box dangerouslySetInnerHTML={{ __html: content }} />
            </StyledPaper>
          </Grid>
        )}
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={() => setPreview(!preview)}
        >
          {preview ? 'Hide Preview' : 'Show Preview'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? <CircularProgress size={24} /> : 'Save & Publish'}
        </Button>
      </Box>
      {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
    </Box>
  );
};

export default CreateBlog; 